import { adminService } from '../services/adminService'

// Sample challenges for testing
const SAMPLE_CHALLENGES = [
  {
    id: 'drink-water',
    name: 'Drink 8 glasses of water',
    description: 'Stay hydrated throughout the day by drinking 8 glasses of water',
    category: 'nutrition-hydration',
    defaultFrequency: {
      mode: 'daily',
      times: ['08:00', '12:00', '16:00', '20:00']
    },
    language: 'en',
    status: 'active',
    icon: '💧',
    order: 1
  },
  {
    id: 'exercise',
    name: 'Daily Exercise',
    description: 'Get at least 30 minutes of physical activity',
    category: 'health-fitness',
    defaultFrequency: {
      mode: 'daily',
      times: ['07:00']
    },
    language: 'en',
    status: 'active',
    icon: '🏃‍♀️',
    order: 2
  },
  {
    id: 'read-book',
    name: 'Read for 30 minutes',
    description: 'Develop your mind by reading books daily',
    category: 'learning-growth',
    defaultFrequency: {
      mode: 'daily',
      times: ['21:00']
    },
    language: 'en',
    status: 'active',
    icon: '📚',
    order: 3
  },
  {
    id: 'meditate',
    name: 'Meditation',
    description: 'Practice mindfulness and meditation',
    category: 'mental-wellness',
    defaultFrequency: {
      mode: 'daily',
      times: ['09:00', '18:00']
    },
    language: 'en',
    status: 'active',
    icon: '🧘‍♀️',
    order: 4
  },
  {
    id: 'no-sugar',
    name: 'Avoid added sugar',
    description: 'Eliminate processed foods with added sugar',
    category: 'nutrition-hydration',
    defaultFrequency: {
      mode: 'daily',
      times: ['12:00']
    },
    language: 'en',
    status: 'active',
    icon: '🍎',
    order: 5
  }
]

// Enhanced categories for better user experience
const SAMPLE_CATEGORIES = [
  {
    id: 'health-fitness',
    name: 'Health & Fitness',
    description: 'Physical wellness, exercise, and body care activities',
    icon: '💪',
    language: 'en',
    status: 'active',
    order: 1
  },
  {
    id: 'nutrition-hydration',
    name: 'Nutrition & Hydration',
    description: 'Healthy eating habits, meal planning, and staying hydrated',
    icon: '🥗',
    language: 'en',
    status: 'active',
    order: 2
  },
  {
    id: 'mental-wellness',
    name: 'Mental Wellness',
    description: 'Mindfulness, meditation, stress management, and emotional health',
    icon: '🧘‍♀️',
    language: 'en',
    status: 'active',
    order: 3
  },
  {
    id: 'learning-growth',
    name: 'Learning & Growth',
    description: 'Personal development, education, skills, and knowledge building',
    icon: '📚',
    language: 'en',
    status: 'active',
    order: 4
  },
  {
    id: 'productivity-career',
    name: 'Productivity & Career',
    description: 'Work efficiency, professional development, and career goals',
    icon: '💼',
    language: 'en',
    status: 'active',
    order: 5
  },
  {
    id: 'relationships-social',
    name: 'Relationships & Social',
    description: 'Family time, friendships, social connections, and communication',
    icon: '👥',
    language: 'en',
    status: 'active',
    order: 6
  },
  {
    id: 'creativity-hobbies',
    name: 'Creativity & Hobbies',
    description: 'Artistic pursuits, creative projects, and recreational activities',
    icon: '🎨',
    language: 'en',
    status: 'active',
    order: 7
  },
  {
    id: 'home-organization',
    name: 'Home & Organization',
    description: 'Household management, cleaning, organizing, and home improvement',
    icon: '🏠',
    language: 'en',
    status: 'active',
    order: 8
  },
  {
    id: 'finance-savings',
    name: 'Finance & Savings',
    description: 'Money management, budgeting, saving, and financial planning',
    icon: '💰',
    language: 'en',
    status: 'active',
    order: 9
  },
  {
    id: 'environment-sustainability',
    name: 'Environment & Sustainability',
    description: 'Eco-friendly habits, conservation, and environmental awareness',
    icon: '🌱',
    language: 'en',
    status: 'active',
    order: 10
  }
]

export async function seedSampleData() {
  console.log('🌱 Starting database seeding...')
  
  try {
    // First, clear existing categories to avoid duplicates
    console.log('🗑️ Clearing existing categories...')
    await adminService.clearAllCategories()
    
    console.log('🔐 Checking admin permissions...')
    
    let categoriesCount = 0
    let challengesCount = 0

    // Seed categories using admin service
    console.log(`📁 Seeding ${SAMPLE_CATEGORIES.length} categories...`)
    
    // Create categories one by one to better handle individual failures
    for (const category of SAMPLE_CATEGORIES) {
      try {
        console.log(`📝 Creating category: ${category.name}`)
        
        // First, create the base category (this will get a random ID)
        const categoryData = {
          icon: category.icon,
          status: category.status,
          order: category.order,
        }
        
        const createdCategoryId = await adminService.createCategory(categoryData)
        console.log(`✅ Created category with ID: ${createdCategoryId}`)

        // Then create the translation using the actual category ID
        const translationData = {
          categoryId: createdCategoryId, // Use the actual created ID
          language: category.language,
          name: category.name,
          description: category.description,
        }
        
        await adminService.createCategoryTranslation(translationData)
        console.log(`✅ Created category translation: ${category.name} (${category.language})`)
        
        categoriesCount++
      } catch (error) {
        console.error(`❌ Failed to create category ${category.name}:`, error)
        
        // Check if it's a permission error
        if (error instanceof Error && error.message.includes('permission')) {
          console.error('🚫 Permission denied! Make sure you are logged in as an admin user.')
          console.error('💡 To fix this:')
          console.error('   1. Make sure your user account has role="admin" in Firestore')
          console.error('   2. Or deploy the updated Firebase security rules')
          console.error('   3. Run: firebase deploy --only firestore:rules')
          throw new Error('Admin permissions required for seeding database')
        }
        
        // Continue with other categories for other types of errors
      }
    }

    // Note: Challenges would be seeded similarly but let's focus on categories first
    console.log(`📝 Skipping challenges for now - focus on categories first`)
    
    if (categoriesCount === 0) {
      throw new Error('No categories were created. Check permissions and try again.')
    }
    
    console.log('✅ Database seeding completed!')
    console.log(`📊 Successfully seeded ${categoriesCount} categories`)
    
    return {
      success: true,
      categoriesCount,
      challengesCount,
      structure: 'normalized'
    }
  } catch (error) {
    console.error('❌ Error seeding database:', error)
    throw error
  }
}

// Add seeding function to global scope for easy access
if (typeof window !== 'undefined') {
  (window as any).seedSampleData = seedSampleData
} 